/*
  Header search dropdown (snippets/joto-search-bar.liquid).
  - Empty field: Recent searches (localStorage) or Popular chips (header setting).
  - Typing: fetches sections/predictive-search-lite.liquid from /search/suggest after 200ms.
  - Suggestions, chips and Enter go straight to the search results page.
*/
(function () {
  if (customElements.get('joto-search')) return;

  var STORAGE_KEY = 'joto-recent-searches';
  var SECTION_ID = 'predictive-search-lite';
  var FIELDS = 'author,tag,title,product_type,variants.title,vendor,variants.sku';
  var cache = {};

  function readRecent() {
    try {
      var list = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return Array.isArray(list) ? list.filter(function (t) { return typeof t === 'string' && t; }) : [];
    } catch (e) {
      return [];
    }
  }

  function writeRecent(list) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch (e) {}
  }

  class JotoSearch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-joto-search-form]');
      this.input = this.querySelector('[data-joto-search-input]');
      this.clearBtn = this.querySelector('[data-joto-search-clear]');
      this.panel = this.querySelector('[data-joto-search-panel]');
      this.chips = this.querySelector('[data-joto-search-chips]');
      this.results = this.querySelector('[data-joto-search-results]');
      this.maxRecent = parseInt(this.dataset.maxRecent, 10) || 3;
      this.showRecent = this.dataset.showRecent !== 'false';
      this.popular = (this.dataset.popular || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      this.active = -1;
      this.term = '';
      // The browser may restore a typed value on reload / back navigation.
      this.clearBtn.hidden = this.input.value.length === 0;

      this.input.addEventListener('input', this.onInput.bind(this));
      this.input.addEventListener('focus', this.open.bind(this));
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.clearBtn.addEventListener('click', this.clear.bind(this));
      this.panel.addEventListener('click', this.onPanelClick.bind(this));
      this.panel.addEventListener('mouseover', this.onPanelHover.bind(this));
      this.onDocClick = this.onDocClick.bind(this);
      document.addEventListener('click', this.onDocClick);
      this.initSpeech();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
    }

    /* ---------- recent searches ---------- */

    saveRecent(term) {
      term = (term || '').trim();
      if (!term || !this.showRecent) return;
      var lower = term.toLowerCase();
      var list = [term].concat(readRecent().filter(function (t) { return t.toLowerCase() !== lower; }));
      writeRecent(list.slice(0, this.maxRecent));
    }

    renderChips() {
      var recent = this.showRecent ? readRecent().slice(0, this.maxRecent) : [];
      var isRecent = recent.length > 0;
      var terms = isRecent ? recent : this.popular;
      this.chips.textContent = '';
      this.chips.hidden = terms.length === 0;
      if (!terms.length) return;

      var label = document.createElement('span');
      label.className = 'joto-search__label';
      label.textContent = isRecent ? 'Recent' : 'Popular';
      this.chips.appendChild(label);

      terms.forEach(function (term) {
        var chip = document.createElement('span');
        chip.className = 'joto-search__chip';
        var link = document.createElement('a');
        link.href = this.searchUrl(term);
        link.textContent = term;
        link.setAttribute('data-joto-search-chip', term);
        chip.appendChild(link);
        if (isRecent) {
          var remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'joto-search__chip-remove';
          remove.setAttribute('aria-label', 'Remove ' + term);
          remove.setAttribute('data-joto-search-remove', term);
          remove.innerHTML = '<span aria-hidden="true">&times;</span>';
          chip.appendChild(remove);
        }
        this.chips.appendChild(chip);
      }, this);

      if (isRecent) {
        var clear = document.createElement('button');
        clear.type = 'button';
        clear.className = 'joto-search__chips-clear';
        clear.textContent = 'Clear';
        clear.setAttribute('data-joto-search-clear-recent', '');
        this.chips.appendChild(clear);
      }
    }

    /* ---------- fetching ---------- */

    onInput() {
      var term = this.input.value.trim();
      this.clearBtn.hidden = this.input.value.length === 0;
      clearTimeout(this.timer);
      if (!term) {
        this.term = '';
        this.results.innerHTML = '';
        this.setActive(-1);
        this.open();
        return;
      }
      this.timer = setTimeout(this.fetchResults.bind(this, term), 200);
    }

    fetchResults(term) {
      this.term = term;
      if (cache[term]) return this.showResults(term, cache[term]);
      if (this.controller) this.controller.abort();
      this.controller = 'AbortController' in window ? new AbortController() : null;

      var url = this.dataset.suggestUrl +
        '?q=' + encodeURIComponent(term) +
        '&section_id=' + SECTION_ID +
        '&resources[type]=' + this.dataset.resourceTypes +
        '&resources[limit]=5&resources[limit_scope]=each' +
        '&resources[options][fields]=' + FIELDS;

      fetch(url, this.controller ? { signal: this.controller.signal } : {})
        .then(function (res) {
          if (!res.ok) throw new Error('Search suggest failed: ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var section = doc.getElementById('shopify-section-' + SECTION_ID);
          var markup = section ? section.innerHTML : '';
          cache[term] = markup;
          this.showResults(term, markup);
        }.bind(this))
        .catch(function () {});
    }

    showResults(term, markup) {
      if (term !== this.input.value.trim()) return;
      this.results.innerHTML = markup;
      this.setActive(-1);
      this.open();
    }

    /* ---------- open / close ---------- */

    open() {
      var hasTerm = this.input.value.trim().length > 0;
      if (!hasTerm) this.renderChips();
      else this.chips.hidden = true;
      var hasContent = (!this.chips.hidden) || (hasTerm && this.results.innerHTML.trim() !== '');
      this.panel.hidden = !hasContent;
      this.input.setAttribute('aria-expanded', hasContent ? 'true' : 'false');
      if (hasContent) this.fitPanel();
    }

    // Keep the whole panel (incl. "See all results") on screen; it scrolls inside if needed.
    fitPanel() {
      var space = window.innerHeight - this.panel.getBoundingClientRect().top - 12;
      this.panel.style.maxHeight = Math.max(space, 240) + 'px';
    }

    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.setActive(-1);
    }

    clear() {
      this.input.value = '';
      this.onInput();
      this.input.focus();
    }

    onDocClick(e) {
      // composedPath() still holds chips that were re-rendered (detached) by the click.
      if (e.composedPath().indexOf(this) === -1) this.close();
    }

    /* ---------- keyboard ---------- */

    options() {
      return Array.prototype.slice.call(this.results.querySelectorAll('[data-joto-search-option]'));
    }

    setActive(index) {
      var options = this.options();
      this.active = index;
      options.forEach(function (option, i) {
        option.id = option.id || this.panel.id + '-option-' + i;
        option.setAttribute('aria-selected', i === index ? 'true' : 'false');
      }, this);
      if (index >= 0 && options[index]) this.input.setAttribute('aria-activedescendant', options[index].id);
      else this.input.removeAttribute('aria-activedescendant');
    }

    onKeydown(e) {
      var options = this.options();
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (this.panel.hidden || !options.length) return;
        e.preventDefault();
        var step = e.key === 'ArrowDown' ? 1 : -1;
        var next = this.active < 0 && step < 0 ? options.length - 1 : (this.active + step + options.length) % options.length;
        this.setActive(next);
      } else if (e.key === 'Enter' && this.active >= 0 && options[this.active] && !this.panel.hidden) {
        e.preventDefault();
        this.go(options[this.active].dataset.term, options[this.active].querySelector('a').href);
      } else if (e.key === 'Escape') {
        if (!this.panel.hidden) this.close();
        else if (this.input.value) this.clear();
      }
    }

    onPanelHover(e) {
      var option = e.target.closest('[data-joto-search-option]');
      if (option) this.setActive(this.options().indexOf(option));
    }

    /* ---------- voice search ---------- */

    // Same browser gate as the theme's old search.js: Chrome only (not Edge).
    initSpeech() {
      var btn = this.querySelector('[data-joto-search-speech]');
      var ua = window.navigator.userAgent.toLowerCase();
      if (!btn || !('webkitSpeechRecognition' in window) || ua.indexOf('chrome') === -1 || !window.chrome || ua.indexOf('edg/') > -1) return;

      var status = this.querySelector('[data-joto-search-speech-status]');
      var recognition = new window.webkitSpeechRecognition();
      var listening = false;
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = document.documentElement.lang || 'en-US';

      var setListening = function (on, message) {
        listening = on;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.classList.toggle('is-listening', on);
        if (status) status.textContent = message || '';
      };

      recognition.addEventListener('start', function () { setListening(true, 'Listening…'); });
      recognition.addEventListener('end', function () { if (listening) setListening(false); });
      recognition.addEventListener('error', function (e) {
        var denied = e.error === 'not-allowed' || e.error === 'service-not-allowed';
        setListening(false, denied ? 'Microphone access is blocked. Type your search instead.' : 'Voice search didn’t catch that. Try again or type your search.');
      });
      recognition.addEventListener('result', function (e) {
        var term = e.results[0][0].transcript;
        setListening(false, 'Searching for ' + term);
        this.input.value = term;
        this.input.focus();
        this.onInput();
      }.bind(this));

      btn.addEventListener('click', function () {
        if (listening) {
          recognition.stop();
          setListening(false);
        } else {
          try { recognition.start(); } catch (err) {}
        }
      });
      btn.hidden = false;
    }

    /* ---------- navigation ---------- */

    searchUrl(term) {
      return this.dataset.searchUrl + '?q=' + encodeURIComponent(term) + '&type=product&options%5Bprefix%5D=last';
    }

    go(term, url) {
      this.saveRecent(term);
      window.location.href = url || this.searchUrl(term);
    }

    onSubmit(e) {
      var term = this.input.value.trim();
      if (!term) {
        e.preventDefault();
        this.input.focus();
        return;
      }
      this.saveRecent(term);
    }

    onPanelClick(e) {
      var target = e.target;
      var remove = target.closest('[data-joto-search-remove]');
      if (remove) {
        e.preventDefault();
        var term = remove.getAttribute('data-joto-search-remove');
        writeRecent(readRecent().filter(function (t) { return t !== term; }));
        this.open();
        this.input.focus();
        return;
      }
      if (target.closest('[data-joto-search-clear-recent]')) {
        e.preventDefault();
        writeRecent([]);
        this.open();
        this.input.focus();
        return;
      }
      var chip = target.closest('[data-joto-search-chip]');
      if (chip) {
        this.saveRecent(chip.getAttribute('data-joto-search-chip'));
        return;
      }
      var option = target.closest('[data-joto-search-option]');
      if (option) {
        this.saveRecent(option.dataset.term);
        return;
      }
      if (target.closest('[data-joto-search-save]')) this.saveRecent(this.input.value);
    }
  }

  customElements.define('joto-search', JotoSearch);
})();
